import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VisitsReportFiltersScreen.module.scss';
import Button from '../../../common/components/button/Button';
import FormItem from '../../../common/components/formItem/FormItem';
import Label from '../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../common/types/SelectValueLabel';
import { Controller, useForm } from 'react-hook-form';
import Input from 'common/components/input/Input';
import Logger from 'common/services/Logger';
import { DATEPICKER_FORMAT_DEFAULT, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import StoppingAreasService from 'api/stoppingAreas/StoppingAreasService';
import Select from 'common/components/select/Select';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import moment from 'moment';

export interface Filters {
    registrationNumber?: string;
    stoppingAreaId?: string;
    stoppingArea?: string;
    entryDate?: Date;
    exitDate?: Date;
    page?: number;
}

type Props = {
    filters: Filters;
    hasCountersPolicy?: boolean;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
}

const VisitsReportFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset, watch, control } = form;
    const [stoppingAreas, setStoppingAreas] = useState<SelectValueLabel[]>([]);

    const getData = async () => {
        try {
            Loading.show();
            const stoppingAreasDB = await StoppingAreasService.catalog();
            setStoppingAreas(stoppingAreasDB);

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get stopping area list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, []);

    const clearFilters = () => {
        reset({
            registrationNumber: undefined,
            stoppingAreaId: undefined,
            stoppingArea: undefined
        });
        onInputChange();
        onSubmit(getValues());
    }

    const onSubmit = (f: Filters) => {
        onFilter(f);
    }

    const onInputChange = () => {
        onChange(getValues());
    }

    const getVal = (arr: any[] | undefined, index: number): any | undefined => {
        return arr && arr.length > index ? arr[index] : undefined;
    }


    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('visits_report.list.registration')}</Label>
                                    <Input
                                        maxLength={255}
                                        name="registrationNumber"
                                        onKeyUp={e => {
                                            setValue('registrationNumber', e.currentTarget.value != '' ? e.currentTarget.value : null);
                                            onInputChange();
                                        }}
                                        placeholder={t('visits_report.list.registration')}
                                        autoComplete='off'
                                        defaultValue={watch('registrationNumber')} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('visits_report.list.stopping_area')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={stoppingAreas}
                                                    isClearable
                                                    placeholder={t('visits_report.list.stopping_area')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('stoppingArea', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={stoppingAreas.find(x => x.value === value) ? { label: stoppingAreas.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="stoppingAreaId"
                                        defaultValue={getValues('stoppingAreaId')} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('visits_report.list.visit_date')}
                                        onChange={(startValue: any, endValue: any) => {
                                            setValue('entryDate', startValue ? moment(startValue).add(1,'hour').toDate() : undefined);
                                            setValue('exitDate', endValue ? moment(endValue).add(1,'hour').toDate() : undefined);
                                            onInputChange();
                                        }}
                                        defaultStartValue={watch('entryDate')}
                                        defaultEndValue={watch('exitDate')}
                                        dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                        isDatePicker
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                </div>

                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.remove')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(VisitsReportFiltersScreen);
